import type { ComponentProps } from 'react';
import { FieldInfo, useFieldContext } from './shared';
import { Label } from '../label';
import { Textarea } from '../textarea';
import { cn } from '../../lib/utils';

export default function TextareaField({
	label,
	className,
	placeholder,
	...props
}: ComponentProps<'textarea'> & { label?: string; variant?: 'default' | 'outline'; size?: 'default' | 'sm' }) {
	const field = useFieldContext<string>();

	// Use the provided placeholder or empty string. When a placeholder is provided,
	// the label should always be floated up to avoid overlap.
	const inputPlaceholder = placeholder ?? '';

	return (
		<div className={className}>
			<div className="text-muted-foreground relative">
				{label && (
					<Label
						data-slot="form-label"
						data-error={field.state.meta.isTouched && !field.state.meta.isValid}
						className={cn(
							'data-[error=true]:text-destructive pointer-events-none w-full truncate px-3.5 py-[17px]',
							// When placeholder is provided, keep label statically floated without animation
							placeholder ? 'translate-y-[-6px] scale-[0.85]' : 'floating',
						)}
						htmlFor={field.name}
					>
						{label}
					</Label>
				)}
				<Textarea
					placeholder={inputPlaceholder}
					id={field.name}
					name={field.name}
					value={field.state.value}
					onBlur={field.handleBlur}
					onChange={(e) => field.handleChange(e.target.value)}
					className={cn('text-foreground min-h-12 px-3 pt-4 pb-2', props.size === 'sm' ? '' : 'min-h-96')}
					{...props}
				/>
			</div>
			<FieldInfo fieldState={field.state} />
		</div>
	);
}
